import { Button as ReactButton } from "devextreme-react/button";
import DateBox, { Button as DateBoxButton } from "devextreme-react/date-box";
import NumberBox from "devextreme-react/number-box";
import { TextBox } from "devextreme-react/text-box";
import React from "react";
import { GrantDto } from "../../../../../dtos/GrantDto";
import { Modules } from "../../../../../dtos/Modules";
import { NotificationTrigger } from "../../../../../dtos/NotificationTrigger";
import { PhaseDto } from "../../../../../dtos/PhaseDto";
import GrantDetailComments from "../../../../grantDetails/GrantDetailComments";
import { StatusInfo } from "../../../Interfaces";
import StatusIcon from "../../../StatusIcon";
import TaskCycleControl from "../TaskCycleControl";
import { PhaseNameHeading } from "./PhaseNameHeading";

export interface IPhaseHeaderViewProps {
    phase: PhaseDto;
    displayName: string;
    grantData?: GrantDto | null;
    module: Modules;
    showStaticEntries: boolean;
    // Column visibility, from the phase's template display settings.
    showProgress: boolean;
    showSubmission: boolean;
    showCompletion: boolean;
    showComments: boolean;
    // Months from the NoA to projected completion; null when the template does not measure from the NoA.
    noaMonths: number | null;
    // Highlights the row while this phase is the one open in the tasklist.
    selected: boolean;
    submission: Date | null;
    completion: Date | null;
    submissionOverridden: boolean;
    completionOverridden: boolean;
    canEditDates: boolean;
    canViewSummary: boolean;
    showSummary: () => void;
    submissionMin?: Date;
    submissionMax?: Date;
    completionMin?: Date;
    changeDate: (field: "submission" | "completion", value: Date | null) => void | Promise<void>;
    restoreDate: (field: "submission" | "completion") => void | Promise<void>;
    onDownloadAll: () => void | Promise<void>;
    // Template setting: whether the Download All icon is offered.
    showDownloadAll: boolean;
    // The task's status, drawn as the dot before the name. With onToggleExpanded it doubles as the
    // expander (minus when open, plus when closed), the same as the tasklist toolbar's.
    statusInfo: StatusInfo;
    expanded: boolean;
    onToggleExpanded?: () => void;
    // Present when the header hosts the start-resubmission icon; called once a cycle is created so
    // the caller can reload the phase and its subtask list. Absent = no icon.
    onCycleStarted?: () => void | Promise<void>;
}

const fmtText = (d?: Date | null) => (d ? new Date(d).toLocaleDateString() : "");

// Column width helper - identical to the legacy GrantPhaseHeader/DCMain so the tasklist row lines up
// with the legacy rows and the DCMain column headers.
const viewClasses = (showStaticEntries: boolean, persist: boolean, contracted: number, expanded: number) =>
    showStaticEntries ? "dx-field col-" + contracted : persist ? "dx-field col-" + expanded : "hideFields";

// Always-visible phase header for every tasklist phase in the module. Mirrors the legacy
// GrantPhaseHeader row (same classes, grid, and Expand/Contract widths) so a converted phase looks
// like the legacy one it replaced - just powered by Phase data + permission-based editing instead of
// the legacy override flag.
export function PhaseHeaderView({
    phase, displayName, grantData, module, showStaticEntries,
    showProgress, showSubmission, showCompletion, showComments, noaMonths, selected,
    submission, completion, submissionOverridden, completionOverridden,
    canEditDates, canViewSummary, showSummary, submissionMin, submissionMax, completionMin,
    changeDate, restoreDate, onDownloadAll, showDownloadAll, statusInfo, expanded, onToggleExpanded, onCycleStarted,
}: IPhaseHeaderViewProps) {

    const toggleHint = expanded ? "Hide subtasks" : "Show subtasks";
    const statusDot = onToggleExpanded ? (
        <span
            role="button"
            tabIndex={0}
            aria-expanded={expanded}
            aria-label={toggleHint}
            title={`${statusInfo.text} - ${toggleHint}`}
            data-testid="phase-header-status"
            style={{ display: "inline-flex", cursor: "pointer" }}
            onClick={(e) => { e.stopPropagation(); onToggleExpanded(); }}
            onKeyDown={(e) => {
                if (e.key !== "Enter" && e.key !== " ") return;
                e.preventDefault();
                e.stopPropagation();
                onToggleExpanded();
            }}
        >
            <StatusIcon info={statusInfo} diameter={22} expandState={expanded ? "expanded" : "collapsed"} />
        </span>
    ) : (
        <span title={statusInfo.text} data-testid="phase-header-status"><StatusIcon info={statusInfo} diameter={22} /></span>
    );

    // Mirror the subtask comment box: only offer the notify checkbox when the task's OnComment
    // config is enabled. Ticking it otherwise sends nothing (TaskCommentEmailService skips on a
    // disabled or missing config).
    const notifyVisible = phase?.defaultPhase?.settings?.notifications?.triggerConfigs?.find(
        (tc) => tc.trigger === NotificationTrigger.OnComment
    )?.enabled ?? false;

    // The revert button doubles as the "overridden" indicator (visible only when overridden), mirroring
    // the legacy header's reset-date icon.
    const editableDate = (
        value: Date | null,
        field: "submission" | "completion",
        overridden: boolean,
        min?: Date,
        max?: Date,
    ) => (
        <div className="grantDetails-created-value">
            <DateBox
                type="date"
                displayFormat="MM-dd-yyyy"
                className={`phaseHeaderDateBox ${canEditDates ? "grantDetails-editable" : "grantDetails-computed"}`}
                value={value ?? undefined}
                disabled={!canEditDates}
                min={min}
                max={max}
                // e.event is set only when a person changed the value; it is absent when the box
                // is reassigned from props. Without this guard a derived date arriving from the
                // server reads as a hand edit, saves itself back, and flags the field overridden.
                onValueChanged={(e) => { if (e.event) changeDate(field, e.value ?? null); }}
            >
                <DateBoxButton
                    name="restore"
                    location="after"
                    options={{
                        icon: "revert",
                        stylingMode: "text",
                        visible: overridden,
                        disabled: !canEditDates,
                        hint: canEditDates ? "Restore the derived date (clears the override)" : "This date was manually overridden",
                        onClick: () => { void restoreDate(field); },
                    }}
                />
                <DateBoxButton name="dropDown" />
            </DateBox>
        </div>
    );

    return (
        <div className="row grantDetailsRowTimeline">
            {/* Name + Download - slightly wider than col-2 (ticket 988). Only this title shades when
                selected, so it reads like a normal tasklist's shaded toolbar rather than the whole
                header row greying out. */}
            <div className="dx-field col-2" style={{ flex: "0 0 21%", maxWidth: "21%" }}>
                {/* Shaded title wrapper. The negative marginLeft pulls the grey box (and the status
                    dot) back past the row's and column's gutters to where the tasklist below
                    starts, so the dot sits where the tasklist toolbar's own dot does for a task
                    without this header; the date columns to the right stay where they are.
                    paddingLeft gives the dot breathing room from the grey's left edge; marginRight
                    holds the grey back from the NoA-months box to its right. Grey matches
                    .task-list-phase-selected (ticket 1015). */}
                <div style={{ marginLeft: "-48px", paddingLeft: "6px", paddingRight: "8px", marginRight: "16px", ...(selected ? { backgroundColor: "#e6e6e6" } : {}) }}>
                {/* Name, then the icons. A flex row rather than fixed columns: the name gives way
                    (ellipsis) and the icons keep their width, so a long name with the cycle pill
                    cannot run over the download icon. */}
                <div className="detailsRow" style={{ display: "flex", alignItems: "center" }}>
                    <div className="grantDetails-created-value" style={{ flex: "1 1 auto", minWidth: 0 }}>
                        {/* The name doubles as the summary trigger - the tasklist toolbar no
                            longer repeats it (ticket 988). */}
                        <PhaseNameHeading
                            name={displayName}
                            onClick={showSummary}
                            cursor={canViewSummary ? "pointer" : "default"}
                            tooltipText={canViewSummary ? "Show summary" : null}
                            leading={statusDot}
                            headingPaddingLeft="10px"
                            headingClassName="dx-field-label-larger"
                            // Legacy renders this label in a plain div - the shared heading is an
                            // h6, so drop the bold to match.
                            headingStyle={{ fontWeight: "normal", paddingTop: "8px", minWidth: 0, overflow: "hidden", textOverflow: "ellipsis" }}
                        />
                    </div>
                    <div className="grantDetails-created-value" style={{ flex: "0 0 auto" }}>
                        <div className="dx-field-label-larger phaseDownloadButton" style={{ display: "flex", alignItems: "center" }}>
                            {/* Icon-only: the cell is too narrow for labels, which just left
                                reserved space to the right of the icon. Hints carry the meaning.
                                Text styling (no contained box) so they sit cleanly on the shaded
                                title instead of showing white button boxes. */}
                            {/* The start-resubmission icon lives here, before download, rather
                                than in the summary: it is a task-level action and this row is
                                the task's header (ticket 1069). */}
                            {onCycleStarted && (
                                <TaskCycleControl phase={phase} grantId={grantData?.id} onResubmitted={onCycleStarted} />
                            )}
                            {showDownloadAll && (
                                <ReactButton icon="download" stylingMode="text" hint="Download all files for this phase" onClick={onDownloadAll} />
                            )}
                        </div>
                    </div>
                </div>
                </div>
            </div>

            {/* NoA + Months (offset) - slightly narrower than a full col-1 (ticket 988). The cell is
                kept (not unmounted) when hidden so the columns to its right stay aligned with the
                DCMain header labels. */}
            <div className={viewClasses(showStaticEntries, false, 1, 0)} style={{ flex: "0 0 4.5%", maxWidth: "4.5%" }}>
                <div className="row detailsRow">
                    <div className="dx-field col-12">
                        <div className="grantDetails-created-value">
                            {showProgress && (
                                <NumberBox className="grantDetails-readonly" value={noaMonths ?? undefined} disabled={true} />
                            )}
                        </div>
                    </div>
                </div>
            </div>

            {/* Dates */}
            <div className={viewClasses(showStaticEntries, true, 5, 4)}>
                <div className="row detailsRow">
                    <div className={viewClasses(showStaticEntries, false, 2, 0)}>
                        <div className="grantDetails-created-value">
                            {showProgress && (
                                <TextBox className="grantDetails-computed" value={fmtText(phase.startDate)} disabled={true} />
                            )}
                        </div>
                    </div>
                    <div className={viewClasses(showStaticEntries, false, 2, 0)}>
                        <div className="grantDetails-created-value">
                            {showProgress && (
                                <TextBox className="grantDetails-computed" value={fmtText(phase.projectedEndDate)} disabled={true} />
                            )}
                        </div>
                    </div>
                    <div className={viewClasses(showStaticEntries, true, 3, 4)}>
                        {showSubmission ? editableDate(submission, "submission", submissionOverridden, submissionMin, submissionMax) : null}
                    </div>
                    <div className={viewClasses(showStaticEntries, true, 3, 4)}>
                        {showCompletion ? editableDate(completion, "completion", completionOverridden, completionMin, undefined) : null}
                    </div>
                    <div className={viewClasses(showStaticEntries, true, 2, 4)}>
                        <div className="grantDetails-created-value">
                            {(showSubmission || showCompletion) && (
                                <DateBox type="date" displayFormat="MM-dd-yyyy" className="grantDetails-computed" value={phase.lastUpdateDate ?? undefined} disabled={true} showDropDownButton={false} />
                            )}
                        </div>
                    </div>
                </div>
            </div>

            {/* Comments - takes whatever the columns before it leave, so the row runs to the right
                edge instead of stopping short of it. */}
            <div className={viewClasses(showStaticEntries, true, 4, 6)} style={{ flex: "1 1 30%", maxWidth: "none" }}>
                <div className="row detailsRow">
                    <div className="dx-field col-12">
                        <div className="grantDetails-created-value">
                            {showComments && grantData?.id && (
                                <GrantDetailComments
                                    displayType="textbox"
                                    grantId={grantData.id}
                                    phaseId={phase.id}
                                    name={phase.name}
                                    module={module}
                                    isReadOnly={false}
                                    notifyVisible={notifyVisible}
                                />
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}
